import { GenericTileProps } from '@christies/react/dist/lib/components/Tiles/GenericTile/GenericTile.types'
import { ComponentStatus } from '@christies/react/typings'
import { dataFetcher } from '../../dataFetcher'
import { useRelatedContentData } from '../../useRelatedContentData'
import { useMockData } from '../../useMockData'
import useSWR from 'swr'
import { ArticleResponse, ParsedArticle, RelatedLotsKey } from './types'
import { sanitizeUrl, parseImageSource } from '../../utils'
import { getBreakpoint } from '@christies/react/utils'

const urlDecoder = ({ url, title, id }: { url: string; title?: string; id?: string }): string => {
  const decoder: Record<string, string | undefined> = {
    '{title}': title,
    '{id}': id,
  }

  return url?.replace(/{title}|{id}/gi, (match) => decoder[match] as string).toLowerCase()
}

export const useRelatedArticlesData = (
  key: RelatedLotsKey,
  mockPromise: Promise<{ default: GenericTileProps[] }>
): { status: ComponentStatus; data?: GenericTileProps[] } => {
  const { storyAPIEndpoint, articlesAPIEndpoint, storyUrlFormat, pageSize } = key

  const { data: mockData, status: mockStatus } = useMockData<GenericTileProps[]>(mockPromise)

  const { data: relatedContentData, status: relatedContentStatus } = useRelatedContentData(
    mockStatus === 'error' ? storyAPIEndpoint : null
  )

  const relatedArticles = relatedContentData?.relatedContent.find((content) => content.type === 'stories')?.values

  const breakpoint = getBreakpoint()

  const ids = relatedArticles
    ?.filter((article, index) => article.type === 'stories' && index < pageSize?.[breakpoint])
    ?.map((article) => article.id)
    .join('|')

  const { data, isLoading, error } = useSWR(
    relatedContentStatus === 'ready'
      ? {
          ...articlesAPIEndpoint,
          parameters: {
            language: articlesAPIEndpoint?.parameters?.language,
            ids: ids,
          },
        }
      : null,
    dataFetcher
  )

  if (mockStatus !== 'error') {
    return { data: mockData, status: mockStatus }
  }

  const sortedData: ParsedArticle[] | undefined = relatedArticles
    ?.map(({ id: sortId }) =>
      (data as ArticleResponse)?.results
        ?.flatMap((article) => {
          const href = urlDecoder({
            url: storyUrlFormat,
            title: sanitizeUrl(article?.storySlug || article?.title),
            id: article?.id,
          })

          const type = article?.tags?.find((tag) => tag?.type === 'storyType')
          const category = article?.tags?.find((tag) => tag?.type === 'storyCategory')
          const image = article?.media.find((pic) => pic.type?.toLowerCase() === 'image')

          return {
            id: article?.id,
            image: {
              src: image?.primary?.href && `${parseImageSource(image?.primary?.href)}?hash=`,
              alt: image?.alt,
              aspectRatio: 8 / 5,
            },
            primaryTitle: article?.title,
            secondaryTitle: article?.subTitleHTML,
            category: category?.values?.[0]?.value,
            type: type?.values?.[0]?.value,
            href,
            thumbnail: article?.flags?.showVideoArrow,
            cta: {
              href,
            },
          }
        })
        ?.find(({ id }) => sortId === id)
    )
    .filter((article) => !!article) as ParsedArticle[] | undefined

  const getComponentStatus = (): ComponentStatus => {
    if (isLoading || relatedContentStatus === 'initial') {
      return 'initial'
    }
    if (error || (!isLoading && !sortedData?.length)) {
      return 'error'
    }
    return 'ready'
  }

  return { status: getComponentStatus(), data: sortedData }
}
